import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Play, Pause, Volume2, VolumeX, Maximize, Clock, 
  ChevronRight, Film, BookOpen, ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * VideoTutorialCard - Displays a video tutorial placeholder
 * In production, this would integrate with actual video hosting
 */
export function VideoTutorialCard({ 
  video, 
  onPlay = () => {},
  compact = false 
}) {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  const handlePlay = () => {
    if (video.videoUrl) {
      onPlay(video);
    } else {
      // Placeholder behavior - show coming soon message
      onPlay({ ...video, isPlaceholder: true });
    }
  };

  const handleDocLink = () => {
    if (video.relatedDocs?.length > 0) {
      navigate(`?doc=${video.relatedDocs[0]}`);
    }
  };

  if (compact) {
    return (
      <div 
        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
        onClick={handlePlay}
        data-testid={`video-card-${video.id}`}
      >
        <div className="w-16 h-12 bg-gradient-to-br from-gray-700 to-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
          <Play className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-gray-900 truncate">{video.title}</p>
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {video.duration}
          </p>
        </div>
      </div>
    );
  }

  return (
    <Card 
      className="overflow-hidden hover:shadow-lg transition-shadow"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`video-card-${video.id}`}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-gradient-to-br from-gray-800 to-gray-900">
        {/* Placeholder thumbnail pattern */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <Film className="w-12 h-12 text-gray-600 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">Video Tutorial</p>
          </div>
        </div>
        
        {/* Play button overlay */}
        <div 
          className={`absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0'}`}
        >
          <Button 
            onClick={handlePlay}
            className="rounded-full w-16 h-16 bg-white/90 hover:bg-white text-gray-900"
          >
            <Play className="w-8 h-8 ml-1" />
          </Button>
        </div>

        {/* Duration badge */}
        <Badge className="absolute bottom-2 right-2 bg-black/70 text-white">
          {video.duration}
        </Badge>

        {/* Priority badge */}
        {video.priority === 1 && (
          <Badge className="absolute top-2 left-2 bg-emerald-500 text-white">
            Essential
          </Badge>
        )}
      </div>

      <CardHeader className="pb-2">
        <CardTitle className="text-lg">{video.title}</CardTitle>
        <CardDescription>{video.description}</CardDescription>
      </CardHeader>

      <CardContent>
        {/* Chapters preview */}
        {video.chapters && video.chapters.length > 0 && (
          <div className="mb-3">
            <p className="text-xs text-gray-500 mb-2">{video.chapters.length} chapters</p>
            <div className="flex flex-wrap gap-1">
              {video.chapters.slice(0, 3).map((chapter, idx) => (
                <Badge key={idx} variant="outline" className="text-xs">
                  {chapter.title}
                </Badge>
              ))}
              {video.chapters.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{video.chapters.length - 3} more
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <Button onClick={handlePlay} className="flex-1">
            <Play className="w-4 h-4 mr-2" />
            Watch Tutorial
          </Button>
          {video.relatedDocs?.length > 0 && (
            <Button variant="outline" onClick={handleDocLink}>
              <BookOpen className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * VideoPlayerModal - Full video player (placeholder)
 */
export function VideoPlayerModal({ 
  video, 
  isOpen, 
  onClose 
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentChapter, setCurrentChapter] = useState(0);

  if (!isOpen || !video) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {/* Video player placeholder */}
        <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden">
          {video.isPlaceholder ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
              <Film className="w-16 h-16 text-gray-500 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Video Coming Soon</h3>
              <p className="text-gray-400 text-center max-w-md">
                This tutorial video is being produced. Check back soon for the full walkthrough!
              </p>
            </div>
          ) : (
            // Actual video player would go here
            <div className="absolute inset-0 flex items-center justify-center">
              <Play className="w-20 h-20 text-white opacity-50" />
            </div>
          )}

          {/* Controls */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-white"
                onClick={() => setIsPlaying(!isPlaying)}
              >
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              </Button>
              
              {/* Progress bar placeholder */}
              <div className="flex-1 h-1 bg-white/30 rounded-full">
                <div className="h-full w-0 bg-emerald-500 rounded-full" />
              </div>

              <span className="text-white text-sm">{video.duration}</span>
              
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-white"
                onClick={() => setIsMuted(!isMuted)}
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </Button>

              <Button variant="ghost" size="sm" className="text-white">
                <Maximize className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Video info */}
        <div className="mt-4 text-white">
          <h2 className="text-xl font-semibold mb-2">{video.title}</h2>
          <p className="text-gray-400 mb-4">{video.description}</p>

          {/* Chapters */}
          {video.chapters && video.chapters.length > 0 && (
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="text-sm font-semibold mb-3">Chapters</h3>
              <div className="space-y-2">
                {video.chapters.map((chapter, idx) => (
                  <button
                    key={idx}
                    className={`w-full flex items-center gap-3 p-2 rounded text-left hover:bg-gray-700 transition-colors ${
                      idx === currentChapter ? 'bg-gray-700' : ''
                    }`}
                    onClick={() => setCurrentChapter(idx)}
                  >
                    <span className="text-emerald-400 text-sm w-12">{chapter.time}</span>
                    <span className="text-sm">{chapter.title}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Close button */}
        <Button 
          variant="outline" 
          className="absolute top-4 right-4 text-white border-white/30 hover:bg-white/10"
          onClick={onClose}
        >
          Close
        </Button>
      </div>
    </div>
  );
}

export default VideoTutorialCard;
